"use client";

import { ChevronRight, MapPin, Plus, Star } from "lucide-react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import {
  Activity,
  type RefObject,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PackForSaleRow } from "@/actions/packs";
import type { FeedResponse } from "@/actions/post";
import type { ShopItemForBuyer } from "@/actions/shop";
import type { EntityStoryStatus } from "@/actions/story";
import type { HiiveBookableNow } from "@/actions/venue-discovery";
import type { PublicVenueSummary } from "@/actions/venue-public";
import {
  ContactDetailsDrawer,
  ContactDetailsRow,
} from "@/components/contact-details-sheet";
import { useOptionalSelectedLocation } from "@/components/location/location-provider.client";
import type { CommentsSheetViewer } from "@/components/posts/comments-sheet";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { DetailInfoRow } from "@/components/ui/detail-info-row";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CardShopSection } from "@/components/wallet/card-shop-section";
import type { SimpleMediaData } from "@/db/schema/hiive";
import { PackSaleCard } from "@/features/packs/components/pack-sale-card";
import { EntityStoryRing } from "@/features/stories/components/entity-story-ring/entity-story-ring.client";
import { StoryHighlightsRow } from "@/features/stories/components/highlights/story-highlights-row.client";
import type { HiiveEventsData } from "@/hooks/use-hiive-data";
import { useHiiveMembers } from "@/hooks/use-hiive-data";
import { getIconComponent, type IconType } from "@/lib/icon-registry";
import type { Coordinates } from "@/lib/location-ranking";
import {
  formatLocationDistance,
  getNearbyLocation,
} from "@/lib/locations/nearby-location";
import { mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import { selectRelevantActivity } from "./hiive-activities.shared";
import { HiiveEventsTabClient } from "./hiive-events-tab.client";
import type { HiiveMemberListItem } from "./hiive-members.shared";
import { HiiveMembersTabClient } from "./hiive-members-tab.client";
import type { Hiive } from "./hiive-page.types";
import { HiivePostsTabClient } from "./hiive-posts-tab.client";
import { HiiveReviewsTabClient } from "./hiive-reviews-tab.client";
import { HiiveVenuesTabClient } from "./hiive-venues-tab.client";

const VenueMap = dynamic(
  () => import("@/components/maps/venue-map").then((mod) => mod.VenueMap),
  {
    ssr: false,
    loading: () => <Skeleton className="h-[220px] w-full rounded-2xl" />,
  },
);

interface HiiveDrawerBodyProps {
  /**
   * `drawer` (phone): the body of the snap-point sheet over the hero, which
   * owns its own scroll. `page` (desktop): the right-hand column of the
   * two-column layout, scrolling with the window and outside any Drawer —
   * so no vaul primitive that needs a Drawer context may render in it.
   */
  layout?: "drawer" | "page";
  hiive: Hiive;
  initialViewerLocation: Coordinates | null;
  hiiveSlug: string;
  viewer: CommentsSheetViewer;
  currentTab: string;
  visibleTabValues: string[];
  onTabChange: (value: string) => void;
  onExpandDrawer: () => void;
  headerHeight: number;
  tabsRef: RefObject<HTMLDivElement | null>;
  minContentHeight: number;
  isReady: boolean;
  snap: number | string | null;
  snapPoints: string[];
  attachRef: (node: HTMLDivElement | null) => void;
  isAuthenticated: boolean;
  initialFeed: FeedResponse;
  initialEvents: HiiveEventsData;
  initialMembers: HiiveMemberListItem[];
  initialVenues: PublicVenueSummary[];
  initialBookableNow: HiiveBookableNow;
  initialShopItems: ShopItemForBuyer[];
  packs: PackForSaleRow[];
  storyRingStatus: EntityStoryStatus | null;
  reviewSummary: { rating: number; count: number } | null;
}

const TAB_LABELS: Record<string, string> = {
  members: "Members",
  events: "Events",
  venues: "Venues",
  shop: "Shop",
  reviews: "Reviews",
  posts: "Posts",
};

/** Prefer the activity of the nearest upcoming event, then the hiive's order. */
function HiiveActivities({
  activities,
  upcoming,
}: {
  activities: Hiive["activities"];
  upcoming: HiiveEventsData["upcoming"];
}) {
  const [open, setOpen] = useState(false);
  const primary = selectRelevantActivity(activities, upcoming);
  if (!primary) return null;
  const PrimaryIcon = getIconComponent(primary.iconKey as IconType);

  return (
    <div className="mt-1 flex items-center gap-2 text-sm text-text-primary">
      <span className="inline-flex items-center gap-1.5">
        {PrimaryIcon ? (
          <PrimaryIcon className="h-5 w-5" aria-hidden="true" />
        ) : null}
        {primary.name}
      </span>
      {activities.length > 1 ? (
        <>
          <button
            type="button"
            aria-label={`View all ${activities.length} activities`}
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="inline-flex min-h-9 min-w-9 items-center justify-center gap-0.5 rounded-full border border-border px-2 font-medium text-text-secondary focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {activities.length - 1}
          </button>
          <Drawer open={open} onOpenChange={setOpen}>
            <DrawerContent className="z-1000 max-h-[85dvh] border-border bg-surface text-text-primary">
              <DrawerHeader className="text-left">
                <DrawerTitle>Activities</DrawerTitle>
                <DrawerDescription>
                  Explore what this hiive does.
                </DrawerDescription>
              </DrawerHeader>
              <ul className="overflow-y-auto px-4 pb-[max(env(safe-area-inset-bottom),2rem)]">
                {activities.map((item) => {
                  const Icon = getIconComponent(item.iconKey as IconType);
                  return (
                    <li
                      key={item.id}
                      className="flex items-center gap-3 border-b border-border py-3"
                    >
                      {Icon ? (
                        <Icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                      ) : null}
                      <span>{item.name}</span>
                    </li>
                  );
                })}
              </ul>
            </DrawerContent>
          </Drawer>
        </>
      ) : null}
    </div>
  );
}

/**
 * About section capped at 4 lines with a Read more drawer, mirroring the
 * event page's "About the event" pattern. Overflow is measured against a
 * hidden unclamped probe at the same width — `scrollHeight > clientHeight`
 * alone never fires under `-webkit-line-clamp` on WebKit/iOS, so the button
 * would never appear there.
 */
function HiiveAboutSection({ description }: { description: string }) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      if ((el.textContent ?? "") !== description) return;
      const width = el.getBoundingClientRect().width;
      const clampedHeight = el.clientHeight;
      if (width < 1 || clampedHeight < 1) return;
      if (el.scrollHeight - clampedHeight > 1) {
        setIsTruncated(true);
        return;
      }
      const styles = window.getComputedStyle(el);
      const probe = document.createElement("div");
      probe.setAttribute("aria-hidden", "true");
      probe.textContent = el.textContent ?? "";
      probe.style.cssText = [
        "position:fixed",
        "left:-99999px",
        "top:0",
        "visibility:hidden",
        "pointer-events:none",
        `width:${width}px`,
        "height:auto",
        "max-height:none",
        "overflow:visible",
        "display:block",
        "-webkit-line-clamp:unset",
        "-webkit-box-orient:initial",
        `font:${styles.font}`,
        `line-height:${styles.lineHeight}`,
        `white-space:${styles.whiteSpace}`,
        "padding:0",
        "margin:0",
        "border:0",
      ].join(";");
      document.body.appendChild(probe);
      const fullHeight = probe.scrollHeight;
      probe.remove();
      setIsTruncated(fullHeight - clampedHeight > 1);
    };
    measure();
    const timers = [
      setTimeout(measure, 50),
      setTimeout(measure, 300),
      setTimeout(measure, 800),
    ];
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.parentElement) observer.observe(el.parentElement);
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    return () => {
      cancelled = true;
      for (const timer of timers) clearTimeout(timer);
      observer.disconnect();
    };
  }, [description]);

  return (
    <>
      <h3 className="mb-1 bg-linear-to-b from-text-primary to-text-primary/60 bg-clip-text text-lg font-semibold text-transparent dark:from-white/90 dark:to-white/30">
        About the Hiive
      </h3>

      <p
        ref={textRef}
        className="line-clamp-4 leading-relaxed whitespace-pre-line text-text-primary"
      >
        {description}
      </p>
      {isTruncated ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm font-semibold text-text-primary dark:text-white"
          onClick={() => setAboutOpen(true)}
        >
          Read more
          <ChevronRight className="h-4 w-4" />
        </button>
      ) : null}

      <Drawer open={aboutOpen} onOpenChange={setAboutOpen}>
        <DrawerContent className="z-1000 max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white">
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              About the Hiive
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-text-secondary dark:text-white/75">
              {description}
            </p>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/**
 * How many members are behind the Members tab, when there are any.
 *
 * Same rule as the event page's Attendees count: zero renders no badge, so a
 * brand-new hiive does not advertise an empty room. Above zero the number is
 * a reason to tap.
 */
function HiiveTabCount({ value }: { value: number }) {
  if (value <= 0) return null;
  return (
    <span className="ml-1.5 text-[0.75em] font-normal text-text-secondary tabular-nums">
      {value > 99 ? "99+" : value}
    </span>
  );
}

export function HiiveDrawerBody({
  layout = "drawer",
  hiive,
  initialViewerLocation,
  hiiveSlug,
  viewer,
  currentTab,
  visibleTabValues,
  onTabChange,
  onExpandDrawer,
  headerHeight,
  tabsRef,
  minContentHeight,
  isReady,
  snap,
  snapPoints,
  attachRef,
  isAuthenticated,
  initialFeed,
  initialEvents,
  initialMembers,
  initialVenues,
  initialBookableNow,
  initialShopItems,
  packs,
  storyRingStatus,
  reviewSummary,
}: HiiveDrawerBodyProps) {
  const [locationOpen, setLocationOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const hiiveContactDetails = useMemo(
    () => ({
      contactPhone: hiive.contactPhone ?? null,
      contactEmail: hiive.contactEmail ?? null,
      websiteUrl: hiive.websiteUrl ?? null,
      socialLinks: hiive.socialLinks ?? {},
    }),
    [
      hiive.contactPhone,
      hiive.contactEmail,
      hiive.websiteUrl,
      hiive.socialLinks,
    ],
  );
  // Start at the same nearest branch as discovery, preserving explicit picks.
  // The standalone /hiives/[slug] page has no AppShell (so no provider); there
  // the server-resolved location is the only one.
  const locationContext = useOptionalSelectedLocation();
  const viewerLocation =
    !locationContext || locationContext.isLoading
      ? initialViewerLocation
      : locationContext.selected;
  const locations = hiive.locations ?? [];
  const nearby = getNearbyLocation(locations, viewerLocation);
  const requestedLocationId = Number(useSearchParams().get("location"));
  const hasManyLocations = locations.length > 1;
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const selectedLocation =
    locations.find((location) => location.id === selectedLocationId) ??
    locations.find((location) => location.id === requestedLocationId) ??
    nearby.location ??
    null;
  const selectedDistanceKm = getNearbyLocation(
    selectedLocation ? [selectedLocation] : [],
    viewerLocation,
  ).distanceKm;

  // Live count for the Members tab badge. Shares the same react-query key as
  // HiiveMembersTabClient, so this is a cache read, not a second fetch — and
  // it falls back to the server-rendered list before that resolves.
  const { data: liveMembers } = useHiiveMembers(hiive.id, initialMembers);
  const memberCount = liveMembers?.length ?? initialMembers.length;

  const isPage = layout === "page";
  // In the drawer the tabs pin inside the sheet's own scroller, tucked under
  // the header; on the page they pin to the window, flush below the top bar.
  const stickyTop = isPage
    ? `${headerHeight}px`
    : headerHeight != null
      ? `max(calc(${Math.max(0, headerHeight - 57)}px - env(safe-area-inset-top, 0px)), 0px)`
      : undefined;
  // Order comes from the page, which ranks bookable tabs first; this only maps
  // those values to labels. Building a second list here is how the visible
  // order silently disagreed with the page's own notion of the default tab.
  const tabItems = visibleTabValues.map((value) => ({
    value,
    label: TAB_LABELS[value] ?? value,
  }));

  return (
    <div
      ref={isPage ? undefined : attachRef}
      className={cn(
        "flex w-full flex-col text-text-primary transition-opacity duration-150",
        isPage
          ? "min-w-0"
          : cn(
              "mx-auto h-full min-h-0 max-w-2xl overflow-x-clip border-t border-background bg-background pb-20",
              snap === snapPoints[1]
                ? "overflow-y-auto pt-[calc(env(safe-area-inset-top)+3.5rem)] side-nav:pt-[max(env(safe-area-inset-top),0.75rem)]"
                : "overflow-hidden",
            ),
        isReady ? "opacity-100" : "opacity-0",
      )}
    >
      <DrawerHeader className={cn("px-3 pt-3", isPage && "pt-0")}>
        <div className="flex items-center gap-3">
          {hiive.logoImage && (
            <EntityStoryRing
              authorType="hiive"
              authorId={String(hiive.id)}
              name={hiive.name}
              avatarUrl={mediaUrlFromAsset(hiive.logoImage)}
              href={null}
              status={storyRingStatus}
              size={60}
            >
              <Image
                src={mediaUrlFromAsset(hiive.logoImage) ?? ""}
                alt={`${hiive.name} logo`}
                width={60}
                height={60}
                className="h-full w-full object-cover"
              />
            </EntityStoryRing>
          )}
          <div className="flex flex-col">
            {isPage ? (
              <h1 className="text-3xl font-semibold text-foreground">
                {hiive.name}
              </h1>
            ) : (
              <DrawerTitle className="text-2xl">{hiive.name}</DrawerTitle>
            )}
            <HiiveActivities
              activities={hiive.activities}
              upcoming={initialEvents.upcoming}
            />
          </div>
        </div>
      </DrawerHeader>

      <div className="mt-4 flex-1 space-y-4 px-3">
        {selectedLocation ? (
          <DetailInfoRow
            icon={MapPin}
            title={
              hasManyLocations
                ? `${locations.length} locations`
                : selectedLocation.label || hiive.location
            }
            subtitle={
              hasManyLocations
                ? [
                    selectedLocation.label,
                    selectedDistanceKm != null
                      ? `${formatLocationDistance(selectedDistanceKm)} away`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : selectedDistanceKm != null
                  ? `${formatLocationDistance(selectedDistanceKm)} away`
                  : "See it on the map"
            }
            onClick={() => setLocationOpen(true)}
          />
        ) : hiive.location ? (
          <DetailInfoRow icon={MapPin} title={hiive.location} />
        ) : null}

        <ContactDetailsRow
          details={hiiveContactDetails}
          subtitle="Reach the hiive and follow along"
          onOpen={() => setContactOpen(true)}
        />

        <DetailInfoRow
          icon={Star}
          title="Reviews"
          subtitle={
            reviewSummary && reviewSummary.count > 0
              ? `★ ${reviewSummary.rating.toFixed(1)} (${reviewSummary.count} ${reviewSummary.count === 1 ? "review" : "reviews"}) · See reviews`
              : "Be the first to review"
          }
          href={`/hiives/${hiiveSlug}/reviews`}
        />

        <StoryHighlightsRow ownerType="hiive" ownerId={String(hiive.id)} />

        <HiiveAboutSection description={hiive.description} />
      </div>

      <Tabs
        value={currentTab}
        onValueChange={onTabChange}
        className="w-full pt-2"
      >
        <div
          ref={tabsRef}
          /* Scrolls sideways rather than clipping. Six tabs plus the members
            count can exceed 375px — "Members 128" on a narrow phone would
            otherwise have its number cut off by `flex-1`'s zero basis, the
            same failure the event page's tab bar already guards against. */
          className="sticky z-40 [scrollbar-width:none] overflow-x-auto bg-background/80 px-3 pt-2 pb-2 backdrop-blur-sm [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{
            top: stickyTop,
          }}
        >
          <TabsList className="flex w-full min-w-max">
            {tabItems.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="min-w-max flex-1"
                onClick={onExpandDrawer}
              >
                {tab.label}
                {tab.value === "members" ? (
                  <HiiveTabCount value={memberCount} />
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div
          // On the page the window scrolls, so a short tab would yank the
          // whole column up under the reader; hold roughly a screen instead.
          className={cn(isPage && "min-h-[calc(100dvh-10rem)] pt-2")}
          style={{
            minHeight:
              !isPage && minContentHeight > 0
                ? `${minContentHeight}px`
                : undefined,
          }}
        >
          <Activity mode={currentTab === "posts" ? "visible" : "hidden"}>
            <Suspense fallback={<Skeleton className="mx-3 h-32 rounded-2xl" />}>
              <HiivePostsTabClient
                hiiveId={hiive.id}
                viewer={viewer}
                initialFeed={initialFeed}
              />
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "members" ? "visible" : "hidden"}>
            <Suspense fallback={<Skeleton className="mx-3 h-32 rounded-2xl" />}>
              <HiiveMembersTabClient
                hiiveId={hiive.id}
                initialMembers={initialMembers}
              />
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "events" ? "visible" : "hidden"}>
            {/* The hiive's packs had no entry point anywhere before this: the
                only links to them were on booking screens that already knew a
                pack applied, which is no way to find out one exists. Above the
                list, because a pack is a decision about the whole list rather
                than about any one thing in it — and in the page's own heading
                and grid, so it reads as a section of this page rather than
                something that wandered in from the wallet. */}
            {packs.length > 0 ? (
              <PacksSection
                packs={packs}
                hiiveSlug={hiiveSlug}
                hiiveName={hiive.name}
                hiiveLogoImage={hiive.logoImage ?? null}
              />
            ) : null}
            <Suspense fallback={<Skeleton className="mx-3 h-32 rounded-2xl" />}>
              <HiiveEventsTabClient
                hiiveId={hiive.id}
                hiiveName={hiive.name}
                hiiveSlug={hiiveSlug}
                initialEvents={initialEvents}
              />
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "venues" ? "visible" : "hidden"}>
            {packs.length > 0 ? (
              <PacksSection
                packs={packs}
                hiiveSlug={hiiveSlug}
                hiiveName={hiive.name}
                hiiveLogoImage={hiive.logoImage ?? null}
              />
            ) : null}
            <Suspense fallback={<Skeleton className="mx-3 h-32 rounded-2xl" />}>
              <HiiveVenuesTabClient
                bookableNow={initialBookableNow}
                hiiveSlug={hiiveSlug}
                initialVenues={initialVenues}
              />
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "shop" ? "visible" : "hidden"}>
            <Suspense fallback={<Skeleton className="mx-3 h-32 rounded-2xl" />}>
              <div className="px-3 pb-4">
                <CardShopSection
                  hiiveId={hiive.id}
                  initialItems={initialShopItems}
                />
                <BackAwareLink
                  href={`/hiives/${hiiveSlug}/shop`}
                  className="mt-4 flex items-center justify-center gap-1 text-sm font-semibold text-brand"
                >
                  See full shop <ChevronRight className="size-4" />
                </BackAwareLink>
              </div>
            </Suspense>
          </Activity>
          <Activity mode={currentTab === "reviews" ? "visible" : "hidden"}>
            <Suspense fallback={<Skeleton className="mx-3 h-32 rounded-2xl" />}>
              <HiiveReviewsTabClient
                hiiveSlug={hiiveSlug}
                hiiveName={hiive.name}
                isAuthenticated={isAuthenticated}
              />
            </Suspense>
          </Activity>
        </div>
      </Tabs>

      <ContactDetailsDrawer
        open={contactOpen}
        onOpenChange={setContactOpen}
        details={hiiveContactDetails}
        subjectLabel={hiive.name}
      />

      <Drawer open={locationOpen} onOpenChange={setLocationOpen}>
        <DrawerContent className="z-1000 max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white">
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
              {hasManyLocations ? "Locations" : "Location"}
            </DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            {selectedLocation ? (
              // Keyed so the map remounts on the picked location — the
              // leaflet container only reads `center` on mount.
              <VenueMap
                key={selectedLocation.id}
                latitude={selectedLocation.latitude}
                longitude={selectedLocation.longitude}
                venueName={selectedLocation.label || hiive.name}
                address={selectedLocation.address ?? hiive.location}
              />
            ) : null}
            {hasManyLocations ? (
              <ul className="space-y-2">
                {locations.map((location) => {
                  const selected = location.id === selectedLocation?.id;
                  const distanceKm = getNearbyLocation(
                    [location],
                    viewerLocation,
                  ).distanceKm;
                  return (
                    <li key={location.id}>
                      <button
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setSelectedLocationId(location.id)}
                        className={cn(
                          "flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors duration-150",
                          selected
                            ? "border-brand bg-brand/5 dark:bg-brand/10"
                            : "border-border hover:bg-surface-alt dark:border-white/[0.1] dark:hover:bg-glass-bg-08",
                        )}
                      >
                        <MapPin
                          className={cn(
                            "mt-0.5 h-5 w-5 shrink-0",
                            selected
                              ? "text-brand"
                              : "text-text-secondary dark:text-white/58",
                          )}
                        />
                        <span className="min-w-0 space-y-1">
                          <span className="block text-base font-medium text-text-primary dark:text-white">
                            {location.label}
                          </span>
                          {distanceKm != null ? (
                            <span className="block text-sm text-text-secondary">
                              {formatLocationDistance(distanceKm)} away
                            </span>
                          ) : null}
                          {location.address ? (
                            <span className="block text-sm text-text-secondary dark:text-white/70">
                              {location.address}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="space-y-1">
                <p className="text-base font-medium text-text-primary dark:text-white">
                  {selectedLocation?.label || hiive.location}
                </p>
                {selectedLocation?.address ? (
                  <p className="text-sm text-text-secondary dark:text-white/70">
                    {selectedLocation.address}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

/**
 * The packs section — the same ticket card as the packs page.
 *
 * One feature, one drawing: the thing on sale here and the thing bought there
 * must read as the same object, or buyers learn the pack twice.
 */
function PacksSection({
  packs,
  hiiveSlug,
  hiiveName,
  hiiveLogoImage,
}: {
  packs: PackForSaleRow[];
  hiiveSlug: string;
  hiiveName: string;
  hiiveLogoImage: SimpleMediaData | null;
}) {
  return (
    <section className="px-3 pb-6">
      <div className="mb-4 flex items-center">
        <div className="h-px flex-1 bg-linear-to-r from-transparent via-border to-border" />
        <h2 className="mx-4 text-xl font-semibold text-text-primary">
          Save with a pack
        </h2>
        <div className="h-px flex-1 bg-linear-to-l from-transparent via-border to-border" />
      </div>
      <ul className="flex flex-col gap-3" aria-label="Packs on sale">
        {packs.map((pack) => (
          <li key={pack.packId}>
            <PackSaleCard
              pack={pack}
              hiiveSlug={hiiveSlug}
              hiiveName={hiiveName}
              hiiveLogoImage={hiiveLogoImage}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
